import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Button,
} from '@mui/material'
import { MarkEmailRead, Delete, DoneAll } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import {
  useNotifications,
  useMarkNotificationRead,
  useDeleteNotification,
} from '../../hooks/useNotifications'
import { format } from 'date-fns'

export default function NotificationsPage() {
  const { t } = useTranslation()
  const { data: notifications = [], isLoading } = useNotifications()
  const markRead = useMarkNotificationRead()
  const deleteNotif = useDeleteNotification()

  const unread = notifications.filter((n) => !n.read)
  const markAllRead = () => {
    unread.forEach((n) => markRead.mutate(n.id))
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={t('notifications.title')}
        subtitle={`${unread.length} ${t('notifications.filterUnread')}`}
        action={
          unread.length > 0 && (
            <Button startIcon={<DoneAll />} onClick={markAllRead}>
              {t('notifications.markAllRead')}
            </Button>
          )
        }
      />

      {notifications.length === 0 ? (
        <Alert severity="info">{t('notifications.noNotifications')}</Alert>
      ) : (
        <Stack spacing={1}>
          {notifications.map((n) => (
            <Card
              key={n.id}
              sx={{
                borderLeft: n.read ? 'none' : '4px solid',
                borderColor: 'primary.main',
                opacity: n.read ? 0.8 : 1,
              }}
            >
              <CardContent
                sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: '12px !important' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" fontWeight={n.read ? 400 : 600}>
                      {n.title}
                    </Typography>
                    <Chip label={n.channel} size="small" variant="outlined" />
                    {!n.read && <Chip label="New" size="small" color="primary" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    {n.created_at ? format(new Date(n.created_at), 'PPp') : '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                  {!n.read && (
                    <Tooltip title={t('notifications.markAllRead')}>
                      <IconButton size="small" onClick={() => { markRead.mutate(n.id) }}>
                        <MarkEmailRead fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" color="error" onClick={() => deleteNotif.mutate(n.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}
